import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import {States} from "../../shared/config/constant.module";
import template from './deletedDefectReport.tpl.html';
import {Plant} from "../../models/navigation";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {StateParams} from "../../common";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import ReportServiceName, {ReportService} from "../../services/reports/report.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import CriticalDefectServiceName, {CriticalDefectService} from "../../services/defects/criticalDefectService";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";

let requires = [LoaderServiceName, ReportServiceName, CriticalDefectServiceName, NavigationServiceName];

interface DeletedDefectReportController {
  plants: Plant[];
  selectedPlant: Plant;
  startDate: Date;
  endDate: Date;
  form: angular.IFormController;

  results: apsViewModels.DeletedDefectReportItem[];
  searchPerformed: boolean;

  generateReport(): void;
  formatTime(time: Date): string;
  openReport(defect: apsViewModels.DeletedDefectReportItem): void;
}

export interface DeletedDefectReportParams extends StateParams {
  reportPlantId: string;
  startDate: string;
  endDate: string;
  autoRun: boolean;
}

class DeletedDefectReportCtrl implements DeletedDefectReportController {
  plants: Plant[];
  selectedPlant: Plant;
  startDate: Date;
  endDate: Date;
  form: angular.IFormController;
  private $stateParams: DeletedDefectReportParams;
  private $state: angular.ui.IStateService;

  results: apsViewModels.DeletedDefectReportItem[];
  searchPerformed: boolean;

  loaderService: LoaderService;
  reportService: ReportService;
  private $q: angular.IQService;
  private criticalDefectService: CriticalDefectService;

  constructor(
    $stateParams: DeletedDefectReportParams,
    $state: angular.ui.IStateService,
    $q: angular.IQService,
    navigationService: NavigationService,
    criticalDefectService: CriticalDefectService,
    reportService: ReportService,
    loaderService: LoaderService
  ) {
    this.$q = $q;
    this.$state = $state;
    this.$stateParams = $stateParams;
    this.loaderService = loaderService;
    this.reportService = reportService;
    this.criticalDefectService = criticalDefectService;

    var today = moment().startOf('day');
    this.startDate = today.clone().subtract(1, 'month').toDate();
    this.endDate = today.toDate();

    var promise = navigationService.getNavigation().then((navModel) => {
      this.plants = navModel.plants();
      this.selectedPlant = navModel.selectedPlant;

      return this.loadState($stateParams);
    });

    loaderService.loadingPromise(promise);
  }

  openReport(defect: apsViewModels.DeletedDefectReportItem): void {
    this.criticalDefectService.openReport(defect.shopId, defect);
  }

  loadState(params: DeletedDefectReportParams): angular.IPromise<any> {
    if (params.reportPlantId && params.reportPlantId !== params.plantId) {
      var plant = _.find(this.plants, {id: params.reportPlantId});
      if (plant) {
        this.selectedPlant = plant;
      }
    }

    if (params.startDate) {
      var startDate = moment(params.startDate);
      if (startDate.isValid()) {
        this.startDate = startDate.toDate();
      }
    }

    if (params.endDate) {
      var endDate = moment(params.endDate);
      if (endDate.isValid()) {
        this.endDate = endDate.toDate();
      }
    }

    if (params.autoRun) {
      return this.runDefectReport();
    }

    return this.$q.when();
  }  

  exportToCsv() { 

    return _.orderBy(_.map<apsViewModels.DeletedDefectReportItem, any>(this.results, (x) => {
      let comments = '';
  
      _.each(x.comments, (c) => {
        comments = `${comments + (comments.length > 0 ? ' - ': '') + c.comment}`;
      });

      return {
        shopName: x.shopName,
        jobRunLegacyId: x.jobRunLegacyId,
        jobDescription: x.jobDescription,
        section: x.section,
        gobName: x.gobName,
        toolNumber: x.toolNumber,
        severity: x.severity,
        defectTypeLegacyId: x.defectTypeLegacyId,
        defectTypeName: x.defectTypeName,
        createdDetail: `${x.createdByFullName} ${x.createdByLastRoleName} ${DateUtilityService.formatTime(x.createdOn, this.selectedPlant.timeZoneId)}`,
        createdOn: x.createdOn,
        comments: comments,
        deletedDetail: `${x.deletedByFullName} ${x.deletedByLastRoleName} ${DateUtilityService.formatTime(x.deletedOn, this.selectedPlant.timeZoneId)}`,
        deletedOn: x.deletedOn,
        reasonDeleted: x.reasonDeleted
      };
    }), ['shopName', 'deletedOn'], ['asc', 'asc']);
  }

  saveState() {
    var reportPlantId = this.selectedPlant ? this.selectedPlant.id : undefined;
    if (reportPlantId === this.$stateParams.plantId) {
      reportPlantId = undefined;
    }
    var params: DeletedDefectReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: reportPlantId,
      startDate: this.startDate ? this.startDate.toISOString() : undefined,
      endDate: this.endDate ? this.endDate.toISOString() : undefined,
      autoRun: true
    };

    this.$state.transitionTo(States.DELETED_DEFECT_REPORT, params);
  }

  generateReport() {
    if (this.form.$invalid) {
      return;
    }

    this.saveState();

    this.loaderService.loadingPromise(this.runDefectReport());
  }

  private runDefectReport(): angular.IPromise<any> {
    var startDateStart = moment(this.startDate).startOf('day').toDate();
    var tzOffsetStartDate = DateUtilityService.localToTimeZone(startDateStart, this.selectedPlant.timeZoneId).toDate();

    var endDateEnd = moment(this.endDate || new Date()).endOf('day').toDate();
    var tzOffsetEndDate = DateUtilityService.localToTimeZone(endDateEnd, this.selectedPlant.timeZoneId).toDate();

    return this.reportService.getDeletedDefectsReport(this.selectedPlant.id, tzOffsetStartDate, tzOffsetEndDate)
      .then(reportItems => {
        this.results = reportItems;
        this.searchPerformed = true;
      });
  }

  formatTime(time: Date): string {
    return DateUtilityService.formatTime(time, this.selectedPlant.timeZoneId);
  }
}

export default angular.module('aps.controller.deletedDefectReport', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.DELETED_DEFECT_REPORT,
      url: '/admin/reports/deletedDefects?startDate&endDate&autoRun&reportPlantId',
      params: {
        startDate: {dynamic: true},
        endDate: {dynamic: true},
        autoRun: {dynamic: true},
        reportPlantId: {dynamic: true},
      },
      controller: 'DeletedDefectReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Deleted Defect Report'
      }
    });
  })
  .controller('DeletedDefectReportCtrl', DeletedDefectReportCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/reports/deletedDefectReport.controller.ts